import React from 'react'
import { Chip, Tooltip } from '@mui/material'
import { type IAttribute } from '../../store/slices/SurveyTemplateSlice'
import { blue } from '../../colors'

interface AttributesFilterChipsProps {
  attributes?: IAttribute[] | null
}

interface ShowAttributeValueProps {
  attr: IAttribute
}

const ShowAttributeValue: React.FC<ShowAttributeValueProps> = ({ attr }) => {
  if (!attr?.values || attr.values.length === 0) {
    return <></>
  }

  // Format the attribute values based on its operation ('is not' or others)
  const modifiedValues = attr.op === 'is not'
    ? attr.values.map(value => `Not ${value}`).slice(0, 2).join(', ')
    : attr.values.slice(0, 2).join(', ')

  // Show additional values if more than 2 exist
  const remainingValues = attr.values.length > 2 ? `+${attr.values.length - 2}` : ''

  return (
    <>
      <b>{attr.key}</b>: {modifiedValues} {remainingValues}
    </>
  )
}

const AttributesFilterChips: React.FC<AttributesFilterChipsProps> = ({ attributes }) => {
  // Preview/API payloads can send attributes: null (common on instructor-added sections).
  // Other call sites already coerce with ?? []; harden here so a single null section cannot
  // take down Survey Preview via ErrorBoundary.
  return (
    <>
      {(attributes ?? []).map((attr, index) => {
        const values = attr?.values ?? []
        const formattedValue = <ShowAttributeValue attr={attr} />

        // Tooltip content for remaining values after the first two
        const tooltipContent = attr.op === 'is not'
          ? values.map((value) => `Not ${value}`).slice(2).join(', ')
          : values.slice(2).join(', ')

        return (
          <Tooltip title={tooltipContent} key={index}>
            <Chip
              label={formattedValue}
              variant="filled"
              color="info"
              size="small"
              sx={{
                borderRadius: '6px',
                border: `1px solid ${blue[150]}`,
                fontWeight: 500,
                color: blue[450],
                backgroundColor: blue[0]
              }}
            />
          </Tooltip>
        )
      })}
    </>
  )
}

export default React.memo(AttributesFilterChips)
